import React, {useEffect, useRef} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Card from 'components/core/Card';
import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Grid from 'components/core/Grid';
import Sheet from 'components/core/Sheet';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import StarIcon from '@mui/icons-material/Star';

import CommonUtils from 'utils/CommonUtils';

import InfoLine from './InfoLine';
import DossierActionButtons from './DossierActionButtons';
import DossierPrintButton from './DossierPrintButton';

import TaxonomyName from 'domain/TaxonomyName';

import alternateIdentifierActions from 'actions/alternateIdentifierActions';


const TaxonomyDossier = ({
    taxonomy, 
    taxonomyNameSynonyms,
    firstObservation,
    lastObservation
}) => {
    const dispatch = useDispatch();
    const alternateIdentifiers = useSelector(state => state.alternateIdentifier.list);
    const dossierRef = useRef();
    
    useEffect(() => {
        if (taxonomy) {
            dispatch(alternateIdentifierActions.list({taxonomy: taxonomy.id}));
        }
    }, [taxonomy]);

    if (!taxonomy || !taxonomy.getByPath) return null;

    const synonyms = taxonomyNameSynonyms.map(synonym => synonym.get('name'))
        .filter(synonym => synonym !== (taxonomy.get ? taxonomy.get('primaryName') : taxonomy.primaryName))
        .join(', ');

    const getParents = (taxon, index) => {
        if (!taxon) return [];
        const taxonData = {
            name: taxon?.get('primaryName'),
            id: taxon.id
        }
        if (!taxon.get('parent')) return [taxonData];
        return [...getParents(taxon.get('parent'), index + 1), taxonData];
    }


    return (
        <Sheet 
            ref={dossierRef}
            sx={{
                width: '100%',
                background: 'none',
                mt: 2,
                mb: 2
            }}
        >
            <Card>
                <Grid container >
                    <Grid item xs={12}>
                        <Stack direction='row' spacing={6} alignItems='end' justifyContent='space-between'>
                            <Stack direction='row' spacing={2} alignItems='end'>
                                <Typography variant='h3'>
                                    TAXON {taxonomy.primaryName}
                                </Typography>
                            </Stack>
                            <Stack direction='row' spacing={3} alignItems='end' style={{marginBottom: 6}} flexWrap="wrap">
                                {alternateIdentifiers
                                    .sort((a, b) => (b.get('primary') || b.primary) - (a.get('primary') || a.primary))
                                    .map((alternateIdentifier, index) => (
                                    <Stack direction='row' key={index} alignItems='center' spacing={0.25}>
                                        {(alternateIdentifier.get('primary') || alternateIdentifier.primary) && (
                                            <StarIcon sx={{ fontSize: '16px', color: 'primary.main' }} />
                                        )}
                                        <Typography 
                                            variant={'body2'}
                                        >
                                            {alternateIdentifier.get('system')?.get('label')}
                                        </Typography>
                                        <Typography 
                                            variant={'body1'}
                                        >
                                            {alternateIdentifier.identifier}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                            <Stack direction='row' spacing={1} className="dossier-action-buttons">
                                <DossierPrintButton 
                                    dossierRef={dossierRef} 
                                    title={`Taxonomy - ${taxonomy.primaryName}`} 
                                />
                                <DossierActionButtons className="Taxonomy" id={taxonomy.id} />
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack sx={{mb: 2}} direction='row' spacing={2}>
                            <Breadcrumbs>
                                {getParents(taxonomy.get('parent'), 0).map((parent, index) => (
                                    <Typography key={index}>
                                        {parent.name}
                                    </Typography>
                                ))}
                            </Breadcrumbs>
                        </Stack>
                    </Grid>
                    <InfoLine 
                        id='subject'
                        label='Subject'
                        value={taxonomy.get('subject')}
                    />
                    <InfoLine
                        id='synonym'
                        label='Synonym'
                        value={synonyms}
                    />
                    <InfoLine
                        id='taxonomyType'
                        label='Taxonomy Type'
                        value={taxonomy.get('taxonomyType')}
                    />
                    <InfoLine
                        id='taxonomyCategory'
                        label='Taxonomy Category'
                        value={taxonomy.get('taxonomyCategory')}
                    />
                    <InfoLine
                        id='securityType'
                        label='Security Type'
                        value={taxonomy.get('securityType')}
                    />
                    <InfoLine
                        id='nativityType'
                        label='Nativity Type'
                        value={taxonomy.get('nativityType')}
                    />
                    <InfoLine
                        id='invasiveStatus'
                        label='Invasive Status'
                        value={taxonomy.get('invasiveStatus')}
                    />
                    <InfoLine
                        id='reproduction'
                        label='Reproduction'
                        value={taxonomy.get('reproduction')}
                    />
                    <InfoLine
                        id='rinhsStatus'
                        label='RINHS Status'
                        value={taxonomy.get('rinhsStatus')}
                    />
                    <InfoLine
                        id='sRank'
                        label='SRank'
                        value={taxonomy.get('sRank')}
                    />
                    <InfoLine
                        id='rigcn'
                        label='RIGCN'
                        value={taxonomy.get('rigcn')}
                    />
                    <Grid item xs={6}>
                    </Grid>
                    {firstObservation && (
                        <InfoLine
                            id='firstObservationDate'
                            label='Date of First Observation'
                            value={CommonUtils.formatDate(firstObservation.get('date'), firstObservation.get('dateSpecificity'))}
                        />
                    )}
                    {lastObservation && (
                        <InfoLine
                            id='lastObservationDate'
                            label='Date of Most Recent Observation'
                            value={CommonUtils.formatDate(lastObservation.get('date'), lastObservation.get('dateSpecificity'))}
                        />
                    )}
                </Grid>
            </Card>
        </Sheet>
    )
} 

TaxonomyDossier.propTypes = {
    taxonomyName: PropTypes.instanceOf(TaxonomyName),
};

export default TaxonomyDossier;